import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import type { Kpi, Anomaly } from '../lib/insights';
import type { GlossaryTerm } from '../lib/glossary';

function safeFilename(title: string): string {
  return (
    title
      .replace(/[^a-z0-9-_]+/gi, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .toLowerCase()
      .slice(0, 80) || 'chart'
  );
}

export async function renderToCanvas(element: HTMLElement): Promise<HTMLCanvasElement> {
  // html2canvas trips on a few CSS patterns that look fine on screen:
  //
  //   1. `truncate` (overflow:hidden + text-overflow:ellipsis) clips the
  //      tops of capital letters because html2canvas calculates the line-box
  //      height slightly tighter than the real glyph extent.
  //   2. `overflow-y-auto` containers hide content beyond the scroll
  //      viewport — when the chart legend is taller than the chart, the
  //      bottom entries get cropped from the export.
  //   3. flex+items-center sometimes positions text against the top edge
  //      of its parent, leaving no room above the cap-line.
  //
  // We aggressively neutralize all three in the cloned DOM via `onclone`,
  // which runs synchronously before rasterization. The original page is
  // unaffected.
  return html2canvas(element, {
    backgroundColor: '#ffffff',
    scale: 2,
    useCORS: true,
    logging: false,
    onclone: (clonedDoc) => {
      // (1) Remove SCROLLABLE overflow (auto / scroll) on non-SVG nodes —
      // these would crop content beyond the scroll viewport during
      // rasterization. We deliberately leave `overflow:hidden` alone since
      // it's used for legitimate visual containment (rounded corners on
      // cards, etc).
      clonedDoc.querySelectorAll<HTMLElement>('*').forEach((el) => {
        const cs = clonedDoc.defaultView?.getComputedStyle(el);
        if (!cs) return;
        const isScrollable =
          cs.overflow === 'auto' ||
          cs.overflow === 'scroll' ||
          cs.overflowY === 'auto' ||
          cs.overflowY === 'scroll' ||
          cs.overflowX === 'auto' ||
          cs.overflowX === 'scroll';
        if (!isScrollable) return;
        if (el.tagName === 'svg' || el.tagName === 'SVG' || el.closest('svg')) return;
        el.style.overflow = 'visible';
        el.style.maxHeight = 'none';
      });

      // (2) Specifically target the chart legend (`<ul aria-label="Chart legend">`)
      // and apply explicit, html2canvas-safe layout to every text row.
      clonedDoc.querySelectorAll<HTMLElement>('[aria-label="Chart legend"]').forEach((legend) => {
        legend.style.maxHeight = 'none';
        legend.style.overflow = 'visible';

        legend.querySelectorAll<HTMLElement>('li').forEach((li) => {
          li.style.minHeight = '28px';
          li.style.height = 'auto';
          li.style.lineHeight = '24px';
          li.style.paddingTop = '3px';
          li.style.paddingBottom = '3px';
          li.style.alignItems = 'center';
          li.style.overflow = 'visible';
        });

        legend.querySelectorAll<HTMLElement>('span').forEach((span) => {
          span.style.lineHeight = '24px';
          span.style.paddingTop = '2px';
          span.style.paddingBottom = '2px';
          span.style.overflow = 'visible';
          span.style.textOverflow = 'clip';
          // Preserve the dot's hard size — only reset overflow on it.
          if (span.getAttribute('aria-hidden') === 'true') {
            span.style.padding = '0';
            span.style.lineHeight = '';
          }
        });
      });

      // (3) Belt-and-suspenders: bump line-height on any text-bearing element
      // whose computed line-height is < 1.3× its font-size. This catches
      // legend variants we might add later without updating this code.
      clonedDoc.querySelectorAll<HTMLElement>('li, span, p, div').forEach((el) => {
        const style = clonedDoc.defaultView?.getComputedStyle(el);
        if (!style) return;
        const lh = parseFloat(style.lineHeight);
        const fs = parseFloat(style.fontSize);
        if (Number.isFinite(lh) && Number.isFinite(fs) && lh > 0 && lh < fs * 1.3) {
          el.style.lineHeight = `${Math.ceil(fs * 1.5)}px`;
        }
      });
    },
  });
}

export async function exportChartAsPNG(element: HTMLElement, title: string): Promise<void> {
  const canvas = await renderToCanvas(element);
  const url = canvas.toDataURL('image/png');
  triggerDownload(url, `${safeFilename(title)}.png`);
}

export async function exportChartAsPDF(element: HTMLElement, title: string): Promise<void> {
  const canvas = await renderToCanvas(element);
  const imgData = canvas.toDataURL('image/png');
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' });
  await drawLetterhead(pdf);
  drawTitleBlock(pdf, title);
  drawFooter(pdf, 1, 1);

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const marginX = 36;
  const contentTop = 110;
  const contentBottom = pageHeight - 40;
  const maxWidth = pageWidth - marginX * 2;
  const maxHeight = contentBottom - contentTop;
  const ratio = Math.min(maxWidth / canvas.width, maxHeight / canvas.height);
  const w = canvas.width * ratio;
  const h = canvas.height * ratio;
  const x = (pageWidth - w) / 2;
  pdf.addImage(imgData, 'PNG', x, contentTop, w, h);

  pdf.save(`${safeFilename(title)}.pdf`);
}

export async function exportConversationAsPDF(
  elements: HTMLElement[],
  title: string,
): Promise<void> {
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' });
  const total = Math.max(1, elements.length);

  for (let i = 0; i < elements.length; i++) {
    const canvas = await renderToCanvas(elements[i]!);
    const imgData = canvas.toDataURL('image/png');
    if (i > 0) pdf.addPage();
    await drawLetterhead(pdf);
    if (i === 0) drawTitleBlock(pdf, title);
    drawFooter(pdf, i + 1, total);

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const marginX = 36;
    const contentTop = i === 0 ? 110 : 80;
    const contentBottom = pageHeight - 40;
    const maxWidth = pageWidth - marginX * 2;
    const maxHeight = contentBottom - contentTop;
    const ratio = Math.min(maxWidth / canvas.width, maxHeight / canvas.height);
    const w = canvas.width * ratio;
    const h = canvas.height * ratio;
    const x = (pageWidth - w) / 2;
    pdf.addImage(imgData, 'PNG', x, contentTop, w, h);
  }
  pdf.save(`${safeFilename(title)}-charts.pdf`);
}

function triggerDownload(href: string, filename: string): void {
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

// ---------------------------------------------------------------------------
// Letterhead — co-branded WB × MS header + confidentiality footer.
// Goal: every PDF the M&A diligence team downloads reads as an official
// document, not an ad-hoc screenshot. Caches both logos so consecutive
// exports don't re-fetch.
// ---------------------------------------------------------------------------

let _wbLogoCache: string | null = null;

async function imgToDataUrl(url: string, mime = 'image/png'): Promise<string> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`fetch ${url} -> ${res.status}`);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(new Blob([blob], { type: mime }));
  });
}

async function getLogos(): Promise<{ wb: string }> {
  if (!_wbLogoCache) {
    try {
      _wbLogoCache = await imgToDataUrl('/walker-blue-logo.png');
    } catch {
      _wbLogoCache = '';
    }
  }
  return { wb: _wbLogoCache };
}

async function drawLetterhead(pdf: jsPDF): Promise<void> {
  const { wb } = await getLogos();
  const pageWidth = pdf.internal.pageSize.getWidth();
  const marginX = 36;

  // Walker Blue mark + wordmark (left)
  if (wb) {
    // Real logo aspect 1471:1156 ≈ 1.27 : 1; render at 24pt height
    pdf.addImage(wb, 'PNG', marginX, 22, 31, 24);
  }
  pdf.setTextColor(30, 58, 138); // wb-800
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.text('Walker Blue', marginX + 38, 33);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8.5);
  pdf.setTextColor(100);
  pdf.text('Intelligence Hub', marginX + 38, 44);

  // Hairline divider
  pdf.setDrawColor(220);
  pdf.setLineWidth(0.5);
  pdf.line(marginX, 56, pageWidth - marginX, 56);
}

function drawTitleBlock(pdf: jsPDF, title: string): void {
  const marginX = 36;
  pdf.setTextColor(15, 23, 42); // slate-900
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(16);
  pdf.text(title, marginX, 80);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8.5);
  pdf.setTextColor(100);
  pdf.text(
    `Generated ${new Date().toLocaleString()}`,
    marginX,
    93,
  );
}

function drawFooter(pdf: jsPDF, page: number, total: number): void {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const marginX = 36;

  pdf.setDrawColor(220);
  pdf.setLineWidth(0.5);
  pdf.line(marginX, pageHeight - 28, pageWidth - marginX, pageHeight - 28);

  pdf.setTextColor(140);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(7.5);
  pdf.text('Confidential — for diligence review only', marginX, pageHeight - 16);
  pdf.text(
    total > 1 ? `Page ${page} of ${total}` : 'Walker Blue Intelligence Hub',
    pageWidth - marginX,
    pageHeight - 16,
    { align: 'right' },
  );
}

// ---------------------------------------------------------------------------
// Diligence pack export — combined PDF with cover + executive summary +
// charts + glossary appendix. The "one document I'd email internally"
// artifact for the M&A diligence audience.
// ---------------------------------------------------------------------------

export interface DiligencePackOptions {
  /** Chart card DOM elements to capture into the body. */
  chartElements: HTMLElement[];
  /** KPIs derived from the chart specs (extractKpis output). */
  kpis: Kpi[];
  /** Anomaly flags derived from the chart specs (detectAnomalies output). */
  anomalies: Anomaly[];
  /** Glossary terms to include as the appendix. Pass [] to skip. */
  glossary: GlossaryTerm[];
  /** Document title — defaults to "Walker Blue Diligence Pack". */
  title?: string;
}

export async function exportDiligencePack(opts: DiligencePackOptions): Promise<void> {
  const title = opts.title ?? 'Walker Blue Diligence Pack';
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' });

  // --- Pre-render all chart canvases first (so we know the page count) ---
  const chartCanvases = await Promise.all(opts.chartElements.map((el) => renderToCanvas(el)));

  // --- Compute total pages: 1 cover + 1 exec summary + N charts + glossary pages
  const glossaryPages = opts.glossary.length === 0 ? 0 : estimateGlossaryPages(opts.glossary);
  const totalPages = 1 + 1 + chartCanvases.length + glossaryPages;

  // --- Page 1: cover ---
  await drawCover(pdf, title, opts);
  drawFooter(pdf, 1, totalPages);

  // --- Page 2: executive summary ---
  pdf.addPage();
  await drawLetterhead(pdf);
  drawExecutiveSummary(pdf, opts);
  drawFooter(pdf, 2, totalPages);

  // --- Pages 3..N+2: charts ---
  for (let i = 0; i < chartCanvases.length; i++) {
    pdf.addPage();
    await drawLetterhead(pdf);
    drawChartPage(pdf, chartCanvases[i]!, i + 1, chartCanvases.length);
    drawFooter(pdf, 3 + i, totalPages);
  }

  // --- Glossary appendix pages ---
  if (opts.glossary.length > 0) {
    await drawGlossaryAppendix(pdf, opts.glossary, totalPages, 3 + chartCanvases.length);
  }

  pdf.save(`${safeFilename(title)}.pdf`);
}

async function drawCover(
  pdf: jsPDF,
  title: string,
  opts: DiligencePackOptions,
): Promise<void> {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const { wb } = await getLogos();

  // Big WB mark, centered
  if (wb) {
    const w = 80;
    pdf.addImage(wb, 'PNG', (pageWidth - w) / 2, pageHeight / 2 - 130, w, 64);
  }

  // Eyebrow + title
  pdf.setTextColor(30, 58, 138);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(9);
  pdf.text('WALKER BLUE INTELLIGENCE HUB', pageWidth / 2, pageHeight / 2 - 50, {
    align: 'center',
  });

  pdf.setTextColor(15, 23, 42);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(28);
  pdf.text(title, pageWidth / 2, pageHeight / 2 - 20, { align: 'center' });

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(11);
  pdf.setTextColor(100);
  pdf.text(
    `Generated ${new Date().toLocaleString()}`,
    pageWidth / 2,
    pageHeight / 2 + 4,
    { align: 'center' },
  );

  // Inclusions summary
  pdf.setFontSize(9);
  pdf.setTextColor(120);
  const summary = `${opts.kpis.length} KPI${opts.kpis.length === 1 ? '' : 's'} · ${opts.chartElements.length} chart${opts.chartElements.length === 1 ? '' : 's'} · ${opts.anomalies.length} anomaly flag${opts.anomalies.length === 1 ? '' : 's'} · ${opts.glossary.length}-term glossary`;
  pdf.text(summary, pageWidth / 2, pageHeight / 2 + 22, { align: 'center' });

}

function drawExecutiveSummary(pdf: jsPDF, opts: DiligencePackOptions): void {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const marginX = 36;
  const contentW = pageWidth - marginX * 2;
  let y = 80;

  // Title
  pdf.setTextColor(15, 23, 42);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(18);
  pdf.text('Executive summary', marginX, y);
  y += 22;

  // KPI section
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(9);
  pdf.setTextColor(80);
  pdf.text('KEY INDICATORS', marginX, y);
  y += 14;

  if (opts.kpis.length === 0) {
    pdf.setFont('helvetica', 'italic');
    pdf.setFontSize(10);
    pdf.setTextColor(140);
    pdf.text('No KPIs derived from the included charts.', marginX, y);
    y += 18;
  } else {
    // Render KPIs in a 3-col layout, each row: label | value | subtitle
    const colW = contentW / 3;
    const rows = Math.ceil(opts.kpis.length / 3);
    for (let r = 0; r < rows; r++) {
      const rowStartY = y;
      let rowMaxH = 0;
      for (let c = 0; c < 3; c++) {
        const i = r * 3 + c;
        const k = opts.kpis[i];
        if (!k) continue;
        const x = marginX + c * colW;

        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(7.5);
        pdf.setTextColor(120);
        pdf.text(k.label.toUpperCase(), x, rowStartY);

        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(16);
        pdf.setTextColor(
          k.tone === 'positive' ? 4 : k.tone === 'negative' ? 153 : k.tone === 'warning' ? 180 : 30,
          k.tone === 'positive' ? 120 : k.tone === 'negative' ? 27 : k.tone === 'warning' ? 83 : 41,
          k.tone === 'positive' ? 87 : k.tone === 'negative' ? 27 : k.tone === 'warning' ? 9 : 99,
        );
        pdf.text(k.value, x, rowStartY + 18);

        if (k.subtitle) {
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(8);
          pdf.setTextColor(120);
          const lines = pdf.splitTextToSize(k.subtitle, colW - 8) as string[];
          pdf.text(lines, x, rowStartY + 30);
          rowMaxH = Math.max(rowMaxH, 30 + lines.length * 9);
        } else {
          rowMaxH = Math.max(rowMaxH, 28);
        }
      }
      y = rowStartY + rowMaxH + 8;
    }
  }

  y += 10;

  // Anomaly section
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(9);
  pdf.setTextColor(80);
  pdf.text('DILIGENCE FLAGS', marginX, y);
  y += 14;

  if (opts.anomalies.length === 0) {
    pdf.setFont('helvetica', 'italic');
    pdf.setFontSize(10);
    pdf.setTextColor(140);
    pdf.text('No anomalies flagged in the included charts.', marginX, y);
    y += 18;
  } else {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    for (const a of opts.anomalies) {
      // Severity dot
      pdf.setFillColor(a.level === 'warning' ? 220 : 245, a.level === 'warning' ? 38 : 158, a.level === 'warning' ? 38 : 11);
      pdf.circle(marginX + 3, y - 4, 2, 'F');
      // Text
      pdf.setTextColor(40);
      const lines = pdf.splitTextToSize(`${a.text}  (from "${a.sourceChartTitle}")`, contentW - 14) as string[];
      pdf.text(lines, marginX + 12, y);
      y += lines.length * 12 + 4;
    }
  }

  y += 10;

  // Charts toc
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(9);
  pdf.setTextColor(80);
  pdf.text(`CHARTS IN THIS PACK · ${opts.chartElements.length}`, marginX, y);
  y += 14;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(120);
  pdf.text(
    'Each chart is rendered on its own page with full detail and source attribution.',
    marginX,
    y,
  );
}

function drawChartPage(
  pdf: jsPDF,
  canvas: HTMLCanvasElement,
  page: number,
  total: number,
): void {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const marginX = 36;
  const contentTop = 80;
  const contentBottom = pageHeight - 40;

  pdf.setTextColor(120);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.text(`Chart ${page} of ${total}`, marginX, 75);

  const imgData = canvas.toDataURL('image/png');
  const maxWidth = pageWidth - marginX * 2;
  const maxHeight = contentBottom - contentTop - 10;
  const ratio = Math.min(maxWidth / canvas.width, maxHeight / canvas.height);
  const w = canvas.width * ratio;
  const h = canvas.height * ratio;
  pdf.addImage(imgData, 'PNG', (pageWidth - w) / 2, contentTop + 5, w, h);
}

function estimateGlossaryPages(terms: GlossaryTerm[]): number {
  // ~6 terms per landscape page given the layout below
  return Math.max(1, Math.ceil(terms.length / 6));
}

async function drawGlossaryAppendix(
  pdf: jsPDF,
  terms: GlossaryTerm[],
  totalPages: number,
  startPageNum: number,
): Promise<void> {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const marginX = 36;
  const contentW = pageWidth - marginX * 2;
  const contentBottom = pageHeight - 40;

  let curPage = startPageNum;
  let y = 80;
  let firstOnPage = true;

  // Bootstrap the first appendix page (page-2-after-charts)
  pdf.addPage();
  await drawLetterhead(pdf);
  drawFooter(pdf, curPage, totalPages);

  pdf.setTextColor(15, 23, 42);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(18);
  pdf.text('Glossary', marginX, y);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(120);
  pdf.text(
    `${terms.length} term${terms.length === 1 ? '' : 's'} from the Walker Blue knowledge vault.`,
    marginX,
    y + 14,
  );
  y += 32;

  for (const term of terms) {
    const longLines = term.long
      ? (pdf.splitTextToSize(term.long, contentW) as string[])
      : [];
    const blockH = 14 + 14 + longLines.length * 11 + 10;

    if (!firstOnPage && y + blockH > contentBottom) {
      curPage += 1;
      pdf.addPage();
      await drawLetterhead(pdf);
      drawFooter(pdf, curPage, totalPages);
      y = 80;
      firstOnPage = true;
    }

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.setTextColor(15, 23, 42);
    pdf.text(term.term, marginX, y);

    pdf.setFont('helvetica', 'italic');
    pdf.setFontSize(9);
    pdf.setTextColor(80);
    pdf.text(term.short, marginX, y + 14);

    if (longLines.length > 0) {
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8.5);
      pdf.setTextColor(110);
      pdf.text(longLines, marginX, y + 26);
    }

    y += blockH;
    firstOnPage = false;
  }
}
